/* eslint-disable complexity */
import { useState, useEffect } from 'preact/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createContext } from 'preact';
import { sendAnswer } from '../apiRequests';
import styleSheet from './styles/scorePredictor.module.scss';
import PredictScreen from './predictScreen.component';
import ResultsScreen from './resultsScreen.component';
import { getFanPrediction } from '../helpers/scorePredictor.helpers';

import SignInGate from '../../components/shared-components/gate/SignInGate.component';

import { DataCaptureFormComponent as Form } from 'playbook-data-capture-sdk-web';

import SubmitButton from '../../components/shared-components/button/SubmitButton';
import { getMultiSelectOffset } from '../../helpers/formOffset';
import { useTranslation } from 'react-i18next';

import { getAuthHeader, checkForSignIn } from '../../helpers/authentication';

export const ScorePredictorContext = createContext({});
const styles = styleSheet.locals || {};

const THREE_AND_A_HALF_SECONDS = 3500;

/**
 * Component for an unopened/scheduled poll
 *
 * @param {object} props score predictor option props
 * @param {object} props.scorePredictor scorePredictor data
 * @param {Function} props.refetch function to refetch latest prediction data
 * @param {object} props.embeddedFormData form data
 * @returns {Function} <ScorePredictorContent />
 */

/**
 * ScorePredictorContent component renders the main content for the score predictor feature.
 *
 * @param {object} props - The component props.
 * @param {object} props.scorePredictor - The score predictor data object.
 * @param {Function} props.refetch - Function to refetch the latest prediction data.
 * @param {object} props.embeddedFormData - The embedded form data object.
 * @param {Function} [props.onDataCaptureFormVisibilityChange] - Optional callback to notify parent when DC form is shown/hidden.
 * @param {boolean} props.isPreview - Are we rendering the admin view preview
 * @param {object} [props.interactionEvent] - InteractionEvent instance for analytics
 * @param {object} [props.analyticsContext] - Resolved analytics context to forward to the embedded data capture form
 * @returns {Element} The rendered ScorePredictorContent component.
 */
function ScorePredictorContent({
    scorePredictor,
    refetch,
    embeddedFormData,
    onDataCaptureFormVisibilityChange,
    isPreview,
    interactionEvent,
    analyticsContext
}) {
    const { t } = useTranslation();
    const [submitError, setSubmitError] = useState(false);
    const [submittingPrediction, setSubmittingPrediction] = useState(false);
    const [editing, setEditing] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const isGated = scorePredictor?.gated;

    const [isSignedIn, setIsSignedIn] = useState(false);

    const authHeader = getAuthHeader();

    // Always compute fanPrediction from the latest scorePredictor prop
    const fanPrediction = getFanPrediction(scorePredictor);

    const isOpen = scorePredictor?.state === 'open';
    const isClosed = scorePredictor?.state === 'closed';
    const isResults = scorePredictor.state === 'resolved';
    const isDraftOrScheduled =
        scorePredictor?.state === 'draft' ||
        scorePredictor?.state === 'scheduled';

    const resultsNoPrediction = isResults && !fanPrediction;

    const [dataCaptureFormSubmitted, setDataCaptureFormSubmitted] =
        useState(false);

    const [revealResult, setRevealResult] = useState(false);

    const hasDataCaptureForm = embeddedFormData?.state === 'open';
    const formOffsetPixels = getMultiSelectOffset(embeddedFormData);

    const showDataCaptureForm =
        embeddedFormData?.state === 'open' &&
        hasDataCaptureForm &&
        isSubmitted &&
        isOpen &&
        !isPreview;

    const isFormBlocking = showDataCaptureForm && !revealResult;

    const canShowPredictByState =
        isOpen || isResults || (isClosed && fanPrediction);

    const canShowResultsByState =
        isClosed || isResults || (isOpen && fanPrediction && !editing);

    const showPredictScreen = canShowPredictByState && !isFormBlocking;

    const showResultsScreen = canShowResultsByState && !isFormBlocking;

    useEffect(() => {
        setIsSignedIn(checkForSignIn());
    }, []);

    // Notify parent when the data capture form visibility changes
    useEffect(() => {
        if (typeof onDataCaptureFormVisibilityChange === 'function') {
            onDataCaptureFormVisibilityChange(
                !!(showDataCaptureForm && !revealResult)
            );
        }
        // Only re-run when these change
    }, [showDataCaptureForm, revealResult, onDataCaptureFormVisibilityChange]);

    useEffect(() => {
        if (isPreview && scorePredictor.previewVoted) {
            setIsSubmitted(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreview, scorePredictor.previewVoted]);

    const onSubmitSuccess = async (userPrediction) => {
        // After submit, wait a bit before refetching to ensure backend is updated
        try {
            await new Promise((resolve) =>
                setTimeout(resolve, THREE_AND_A_HALF_SECONDS)
            );
            await refetch();
            setIsSubmitted(true);
            setSubmittingPrediction(false);
            setEditing(false); // Reset editing so user sees results after submit
            console.warn('answer submitted and data refreshed', userPrediction);
        } catch (error) {
            setSubmittingPrediction(false);
            setEditing(false); // Also reset editing on error to avoid stuck state
            console.warn('answer submitted but refresh failed', userPrediction);
        }
    };

    const submitPrediction = (userPrediction) => {
        setSubmittingPrediction(true);
        // Handle submit logic here, e.g., send scores to an API
        console.warn('Scores submitted:', userPrediction);

        const answerPayload = userPrediction;

        // Sends API requests
        sendAnswer(
            scorePredictor.prediction_id,
            answerPayload,
            isGated,
            authHeader
        )
            .then(() => {
                // Fire analytics event for successful prediction submission
                interactionEvent?.send('PREDICTION_SUBMITTED');
                onSubmitSuccess(answerPayload);
            })
            .catch((error) => {
                setSubmitError(true);
                setSubmittingPrediction(false);
                console.error(error.detail ?? error.message);
            });
    };

    if (submitError) {
        console.warn(submitError);
    }

    return (
        <ScorePredictorContext.Provider
            value={{
                submitPrediction,
                submittingPrediction,
                isPreview,
                isSignedIn,
                authHeader,
                isGated,
                interactionEvent
            }}
        >
            {showDataCaptureForm && !revealResult && (
                <div
                    part="score-predictor-form"
                    className={styles.scorePredictorFormWrapper}
                    style={
                        formOffsetPixels
                            ? {
                                  paddingBottom: formOffsetPixels
                              }
                            : {}
                    }
                >
                    <Form
                        formData={embeddedFormData}
                        submittedCallback={() => {
                            setDataCaptureFormSubmitted(true);
                        }}
                        pollId={scorePredictor.prediction_id}
                        analyticsContext={analyticsContext}
                    />

                    {dataCaptureFormSubmitted && (
                        <div
                            part="score-predictor-form-button"
                            className={styles.scorePredictorFormButton}
                        >
                            <SubmitButton
                                onSubmit={() => {
                                    setRevealResult(true);
                                }}
                                buttonText={t('scorePredictor.formContinue')}
                                title="View prediction results"
                            />
                        </div>
                    )}
                </div>
            )}

            <div
                part="score-predictor-screens"
                className={styles.scorePredictorScreens}
            >
                {!isSignedIn && isGated && !isDraftOrScheduled && (
                    <SignInGate />
                )}
                {showPredictScreen && (
                    <div
                        part="score-predictor-predict"
                        className={classNames(styles.scorePredictorPredict, {
                            [styles.scorePredictorPredictHideDesktop]:
                                resultsNoPrediction
                        })}
                    >
                        <PredictScreen
                            scorePredictor={scorePredictor}
                            fanPrediction={editing ? null : fanPrediction}
                            onEdit={() => setEditing(true)}
                        />
                    </div>
                )}

                {showResultsScreen && (
                    <div
                        part="score-predictor-results"
                        className={styles.scorePredictorResults}
                    >
                        <ResultsScreen
                            scorePredictor={scorePredictor}
                            fanPrediction={fanPrediction}
                        />
                    </div>
                )}
            </div>
        </ScorePredictorContext.Provider>
    );
}

ScorePredictorContent.propTypes = {
    scorePredictor: PropTypes.bool,
    scorePredictorScheduled: PropTypes.bool,
    onCountdownEnd: PropTypes.func.isRequired,
    refetch: PropTypes.func.isRequired,
    embeddedFormData: PropTypes.object,
    onDataCaptureFormVisibilityChange: PropTypes.func,
    isPreview: PropTypes.bool,
    interactionEvent: PropTypes.object,
    analyticsContext: PropTypes.object
};

export default ScorePredictorContent;
